import { ArrowForward } from "@mui/icons-material";
import { Box } from "@mui/material";
import { App, Route, UniversalAppState } from "@pimo/pimo-app-builder";
import {
  CustomFormFields,
  DefaultOverlayPartialAppState,
  PimoFormDialog,
  PMPopUp,
} from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { get, type UseFormReturn } from "react-hook-form";
import { generatePath } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  APP_ITEM_SOURCES,
  APP_SOURCES,
  BasicItem,
  Configuration,
  FormBlock,
  type FormData as ItemFormData,
  getEntityRefTarget,
  Project,
  ReportingItem,
} from "vmd-types";
import {
  DC_RELEVANCE_BLOCK_TITLE,
  getEntityRefFormKey,
  getReportingItemSeedValues,
  getReviewStageName,
  isBlockVisible,
  splitEntityPayload,
  validateBasicItemForm,
} from "vmd-utils";

import { TestAppState } from "../app";
import { Badge } from "../components/badge";
import { ProgressPieBadge } from "../components/progress-pie-badge";
import { APP_ROUTES } from "../constants";
import { createBasicItem, deleteBasicItem } from "../helpers/fetch-basic-items";
import { fetchEntityConfiguration } from "../helpers/fetch-entity-configurations";
import { fetchOverlay } from "../helpers/fetch-overlay";
import { fetchOverview } from "../helpers/fetch-overview";
import { fetchProject } from "../helpers/fetch-project";
import { fetchSettings } from "../helpers/fetch-settings";
import { updateTrackingItem } from "../helpers/fetch-tracking-item";
import { showSnackbar } from "../helpers/show-snackbar";
import { getItemCreationDisplayName } from "../utils/get-item-creation-display-name";
import {
  FormMethodsLike,
  setupFormFieldWatcher,
} from "../utils/setup-form-field-watcher";
import {
  ACTION_BUTTON_FIELD_TYPE,
  ActionButtonFormField,
} from "./custom-form-fields/action-button-field";
import {
  CUSTOM_ID_TYPEAHEAD_FIELD_TYPE,
  CustomIdTypeaheadFormField,
} from "./custom-form-fields/custom-id-typeahead-field";
import {
  REPEATABLE_INPUT_FIELD_TYPE,
  RepeatableInputFormField,
} from "./custom-form-fields/repeatable-input-field";
import {
  applyReviewBlockColors,
  getDisplayCompletionStatus,
} from "./questionnaire/block-completion-indicator";
import {
  DC_RELEVANCE_IMPORT_INFO_FIELD_TYPE,
  DcRelevanceImportInfoField,
} from "./questionnaire/dc-relevance-import-info-field";
import { buildEntityRefSourceOverrides } from "./questionnaire/entity-ref-source-overrides";
import {
  QUESTIONNAIRE_BLOCK_COMPLETION_HINT_FIELD_TYPE,
  QuestionnaireBlockCompletionHintField,
} from "./questionnaire/questionnaire-block-completion-hint-field";
import {
  QUESTIONNAIRE_BLOCK_HEADER_FIELD_TYPE,
  QuestionnaireBlockHeaderField,
} from "./questionnaire/questionnaire-block-header-field";
import {
  buildBoundFormKeys,
  buildQuestionnaireBlockFields,
} from "./questionnaire/questionnaire-fields-builder";
import {
  QUESTIONNAIRE_SUBMISSION_STATUS_FIELD_TYPE,
  QuestionnaireSubmissionStatusField,
} from "./questionnaire/questionnaire-submission-status-field";

const SUBMISSION_BLOCK_CODE = "submission-block";
const LEGACY_SUBMISSION_BLOCK_CODE = "b9";
const DC_RELEVANCE_COMMENT_FIELD_NAME = "formData.dcRelevanceComment";
const DC_RELEVANCE_COMMENT_PLACEHOLDER =
  "Share any thoughts - whether its about the questions, the answers provided, or anything else you’d like us to know.";

// True only for an item this same user created manually (not via import):
// that creator gets full edit/delete rights on it, bypassing the usual OE
// read-only restrictions. Emails are compared case-insensitively since the
// identity provider does not guarantee stable casing across sessions.
function isOwnManualTrackingItem(
  currentTrackingItem: BasicItem | undefined,
  userEmail: string | undefined
): boolean {
  return (
    currentTrackingItem?.source === APP_ITEM_SOURCES.MANUAL &&
    !!userEmail &&
    currentTrackingItem?.createdBy?.trim().toLowerCase() ===
      userEmail.trim().toLowerCase()
  );
}

// For a manually-created item, appends "Created by: {email}" to the review
// title. Imported items show nothing extra since there is no creator.
function getCreatedBySuffix(
  currentTrackingItem: BasicItem | undefined
): string {
  if (currentTrackingItem?.source !== APP_ITEM_SOURCES.MANUAL) return "";
  return currentTrackingItem?.createdBy
    ? ` | Created by: ${currentTrackingItem.createdBy}`
    : "";
}

type DynamicQuestionnaireValueReader = (fieldName: string) => unknown;

function getQuestionnaireFieldValue(
  formMethods: Pick<UseFormReturn<Project>, "getValues"> | undefined,
  fieldName: string
): unknown {
  const getValues = formMethods?.getValues as
    DynamicQuestionnaireValueReader | undefined;
  return getValues?.(fieldName);
}

function getQuestionnaireWatcherFormMethods(
  formMethods: UseFormReturn<Project> | undefined
): FormMethodsLike | undefined {
  if (!formMethods) return undefined;

  return {
    watch: (callback) =>
      formMethods.watch((values, info) =>
        callback(values, { name: info.name })
      ),
  };
}

const CUSTOM_FIELDS = {
  [ACTION_BUTTON_FIELD_TYPE]: ActionButtonFormField,
  [CUSTOM_ID_TYPEAHEAD_FIELD_TYPE]: CustomIdTypeaheadFormField,
  [REPEATABLE_INPUT_FIELD_TYPE]: RepeatableInputFormField,
  [DC_RELEVANCE_IMPORT_INFO_FIELD_TYPE]: DcRelevanceImportInfoField,
  [QUESTIONNAIRE_BLOCK_HEADER_FIELD_TYPE]: QuestionnaireBlockHeaderField,
  [QUESTIONNAIRE_BLOCK_COMPLETION_HINT_FIELD_TYPE]:
    QuestionnaireBlockCompletionHintField,
  [QUESTIONNAIRE_SUBMISSION_STATUS_FIELD_TYPE]:
    QuestionnaireSubmissionStatusField,
} as const satisfies CustomFormFields<
  | typeof ACTION_BUTTON_FIELD_TYPE
  | typeof CUSTOM_ID_TYPEAHEAD_FIELD_TYPE
  | typeof REPEATABLE_INPUT_FIELD_TYPE
  | typeof DC_RELEVANCE_IMPORT_INFO_FIELD_TYPE
  | typeof QUESTIONNAIRE_BLOCK_HEADER_FIELD_TYPE
  | typeof QUESTIONNAIRE_BLOCK_COMPLETION_HINT_FIELD_TYPE
  | typeof QUESTIONNAIRE_SUBMISSION_STATUS_FIELD_TYPE
>;

export async function buildTrackingItemFormRoute(
  app: App<TestAppState>,
  parentRoute: Route<"id" | "viewname", TestAppState>
) {
  const dialog = app.createView({
    name: "add-tracking-item",
  });
  let projectId: string | undefined;
  let trackingItemId: string | undefined;
  let distributionChannel: string | undefined;
  /** Slugs in sidebar order: one per enabled block (block.code ?? block.id). */
  let orderedTabSlugs: string[] = [];
  let currentTabSlugIndex = 0;
  // Kept per tracking item for the page session, so cancelling and reopening a
  // review does not grey out the blocks already worked through.
  const visitedBlockSlugsByItem = new Map<string, Set<string>>();
  let visitedBlockSlugs = new Set<string>();
  let blockTabs: ReturnType<typeof editForm.addFormTab>[] = [];
  let stopQuestionnaireFieldWatcher: (() => void) | undefined;
  let questionnaireTabsBuildChain: Promise<void> = Promise.resolve();
  let rebuildQuestionnaireTabs: () => Promise<void> = async () => {};
  let questionnaireFormInitializationSerial = 0;
  /** Set once Submit has been refused for validation errors; clears on a successful save. */
  let submitBlocked = false;
  // Save and Submit both reach the route as the same `submit` event. PimoForm's
  // `getLastActionAttempted()` can't tell them apart either (Submit sets the
  // same value Save & Close does), so this tracks the Submit press itself; it
  // expires and is cleared by every other action so an aborted press can't leak.
  let submitPressedAt: number | undefined;
  const SUBMIT_INTENT_TTL_MS = 5_000;

  const markSubmitPressed = () => {
    submitPressedAt = Date.now();
  };
  const clearSubmitPressed = () => {
    submitPressedAt = undefined;
  };
  const consumeSubmitPressed = (): boolean => {
    const pressed =
      submitPressedAt !== undefined &&
      Date.now() - submitPressedAt < SUBMIT_INTENT_TTL_MS;
    clearSubmitPressed();
    return pressed;
  };
  const isActivationKey = (event: { key: string }) =>
    event.key === "Enter" || event.key === " ";
  // Buttons activate on keyup, not keydown, so both are covered on both sides.
  const submitPressedHandlers = {
    onMouseDown: markSubmitPressed,
    onKeyDown: (event: { key: string }) =>
      isActivationKey(event) && markSubmitPressed(),
    onKeyUp: (event: { key: string }) =>
      isActivationKey(event) && markSubmitPressed(),
  };
  const clearSubmitPressedHandlers = {
    onMouseDown: clearSubmitPressed,
    onKeyDown: (event: { key: string }) =>
      isActivationKey(event) && clearSubmitPressed(),
    onKeyUp: (event: { key: string }) =>
      isActivationKey(event) && clearSubmitPressed(),
  };

  const isLlmSuggestedSource = (source?: string) =>
    source?.trim().toLowerCase() === APP_SOURCES.LLM_SUGGESTED.toLowerCase();

  const getInitialLlmAnswersByQuestionId = (
    blocks: FormBlock[],
    currentTrackingItem?: BasicItem,
    currentReportingItem?: ReportingItem
  ): Record<string, unknown> => {
    const initial: Record<string, unknown> = {};

    for (const block of blocks) {
      for (const question of block.questions ?? []) {
        if (!isLlmSuggestedSource(question.source)) continue;

        const ref =
          question.entityRef && getEntityRefTarget(question.entityRef)
            ? question.entityRef
            : undefined;

        if (ref) {
          initial[question.id] = (
            currentTrackingItem as unknown as
              Record<string, unknown> | undefined
          )?.[getEntityRefFormKey(ref)];
          continue;
        }

        initial[question.id] = (
          currentReportingItem?.formData as Record<string, unknown> | undefined
        )?.[question.id];
      }
    }

    return initial;
  };

  function syncUrlToCurrentTab() {
    const slug = orderedTabSlugs[currentTabSlugIndex];
    const base = window.location.pathname.split(
      `/tracking-item/${trackingItemId ?? ""}`
    )[0];
    const newUrl =
      !slug || slug === orderedTabSlugs[0]
        ? `${base}/tracking-item/${trackingItemId}`
        : `${base}/tracking-item/${trackingItemId}/${slug}`;

    window.history.replaceState(null, "", newUrl);
  }
  const editForm = new PimoFormDialog<Project, typeof CUSTOM_FIELDS>();
  editForm.registerFormField(
    ACTION_BUTTON_FIELD_TYPE,
    CUSTOM_FIELDS[ACTION_BUTTON_FIELD_TYPE]
  );
  editForm.registerFormField(
    CUSTOM_ID_TYPEAHEAD_FIELD_TYPE,
    CUSTOM_FIELDS[CUSTOM_ID_TYPEAHEAD_FIELD_TYPE]
  );
  editForm.registerFormField(
    REPEATABLE_INPUT_FIELD_TYPE,
    CUSTOM_FIELDS[REPEATABLE_INPUT_FIELD_TYPE]
  );
  editForm.registerFormField(
    QUESTIONNAIRE_BLOCK_HEADER_FIELD_TYPE,
    CUSTOM_FIELDS[QUESTIONNAIRE_BLOCK_HEADER_FIELD_TYPE]
  );
  editForm.registerFormField(
    DC_RELEVANCE_IMPORT_INFO_FIELD_TYPE,
    CUSTOM_FIELDS[DC_RELEVANCE_IMPORT_INFO_FIELD_TYPE]
  );
  editForm.registerFormField(
    QUESTIONNAIRE_BLOCK_COMPLETION_HINT_FIELD_TYPE,
    CUSTOM_FIELDS[QUESTIONNAIRE_BLOCK_COMPLETION_HINT_FIELD_TYPE]
  );
  editForm.registerFormField(
    QUESTIONNAIRE_SUBMISSION_STATUS_FIELD_TYPE,
    CUSTOM_FIELDS[QUESTIONNAIRE_SUBMISSION_STATUS_FIELD_TYPE]
  );

  const form = dialog.addComponent({ component: editForm });

  registerFormEvents();

  function registerFormEvents() {
    // Submit refused to finalize: surface the overarching error next to the
    // per-block error markers the validation pass just set.
    form.on("submit-blocked", () => {
      submitBlocked = true;
      clearSubmitPressed();
      forceFormRerender();
    });

    form.on("submit-without-changes", ({ payload }) =>
      handleSubmit(payload as BasicItem | undefined)
    );
    form.on("submit", ({ payload }) =>
      handleSubmit(payload as BasicItem | undefined)
    );

    async function handleSubmit(payloadInput?: BasicItem) {
      const payload = payloadInput ?? ({} as BasicItem);
      let saved: boolean;
      submitBlocked = false;

      const isFinalSubmit = consumeSubmitPressed();
      let savedProject: Project | undefined;
      const currentProject =
        app.getAppState().editForm?.currentProject ??
        app.getAppState().currentReport?.project;

      // project.* keys mirror the OE read-only, and formData is its own branch;
      // drop both so only entity columns are left to route.
      const flatOf = (source: object): Record<string, unknown> => {
        const flat: Record<string, unknown> = { ...source };
        delete flat.project;
        delete flat.formData;
        delete flat.validateFormData;
        return flat;
      };

      if (payload.flag === "create") {
        const { formData } = payload as BasicItem & {
          formData?: ItemFormData;
        };
        const { basicItem, reportingItem } = splitEntityPayload(
          flatOf(payload)
        );
        const channel =
          (reportingItem.distributionChannel as string | undefined) ||
          distributionChannel;
        saved = Boolean(
          await createBasicItem(
            basicItem as unknown as BasicItem,
            currentProject?.id,
            channel,
            reportingItem as Partial<ReportingItem>,
            formData
          )
        );
      } else {
        // Use the new unified tracking-item endpoint for updates
        const { formData } = payload as BasicItem & {
          formData?: ItemFormData;
        };
        const { basicItem, reportingItem } = splitEntityPayload(
          flatOf(payload)
        );

        // Find the reporting item ID for this tracking item
        const existingReportingItem = currentProject?.reportingItems?.find(
          (ri) => String(ri.basicItem?.id) === String(trackingItemId)
        );

        savedProject = await updateTrackingItem(
          currentProject?.id ?? "",
          existingReportingItem?.id ?? "",
          {
            // basicItem always contains at least the `id` field from BasicItem,
            // so > 1 means there are actual field updates beyond just the id.
            ...(Object.keys(basicItem).length > 1 && {
              basicItem: basicItem as Partial<BasicItem>,
            }),
            ...(Object.keys(reportingItem).length > 0 && {
              reportingItem: reportingItem as Partial<ReportingItem>,
            }),
            ...(formData && { formData }),
            ...(isFinalSubmit && { submitted: true }),
          }
        );
        saved = Boolean(savedProject);
      }
      // Don't claim success / close the form when the save actually failed (4xx/5xx/network).
      if (!saved) {
        showSnackbar(
          app as unknown as App<
            UniversalAppState & DefaultOverlayPartialAppState
          >,
          "Your changes could not be saved. Please try again.",
          "error"
        );
        return;
      }
      const [overlay, overview, settings] = await Promise.all([
        fetchOverlay(),
        fetchOverview(),
        fetchSettings(),
      ]);

      const currentReport = { project: savedProject };

      const updatedProject = currentReport?.project;
      const updatedReportingItem = updatedProject?.reportingItems?.find(
        (item) => String(item.basicItem?.id) === String(trackingItemId)
      );

      app?.patchAppState({
        overlay,
        overview,
        currentReport,
        editForm: {
          ...app.getAppState().editForm,
          currentProject: updatedProject,
          currentReportingItem: updatedReportingItem,
        },
        settings: { ...app.getAppState().settings, ...settings },
      });

      showSnackbar(
        app as unknown as App<
          UniversalAppState & DefaultOverlayPartialAppState
        >,
        "All changes have been saved.",
        "success"
      );

      // A create has no reporting item to keep editing against: the form would
      // stay open still flagged `create` and the next Save would add a second
      // item. Only an update can stay open.
      if (payload.flag === "create") {
        closeEditForm();
        return;
      }

      // Save and Save & Next leave the form open, so the saved values become the
      // new baseline: without this the form stays dirty and Save stays enabled.
      // Closing is driven by the form's own `close` event (Submit / Cancel).
      // This also clears RHF's dirtyFields, which is what lets a block's green
      // indicator switch from frozen back to live (see block-completion-indicator.ts).
      const formMethods = editForm.getFormMethods();
      formMethods?.reset(formMethods.getValues());
      await rebuildQuestionnaireTabs();
    }

    form.on("cancel", () => closeEditForm());
    form.on("delete", () =>
      app.patchAppState({ showDeleteOwnTrackingItemDialog: true })
    );
    form.on("cancel-with-changes", () =>
      app.patchAppState({ showEditFormConfirmationDialog: true })
    );
    form.on("close", () => closeEditForm());
  }
  // The hint fields read `submitBlocked` through a getter, so the form only picks
  // the change up once new props arrive: a fresh editForm identity does that.
  function forceFormRerender() {
    app.patchAppState({
      editForm: { ...app.getAppState().editForm },
    });
  }

  function closeEditForm() {
    submitBlocked = false;
    questionnaireFormInitializationSerial++;
    stopQuestionnaireFieldWatcher?.();
    stopQuestionnaireFieldWatcher = undefined;
    if (trackingItemId) {
      const resourceLockPlugin =
        app.getPluginByName<ResourceLockPlugin<TestAppState>>(
          "ResourceLockPlugin"
        );

      resourceLockPlugin?.unlock(`item-${trackingItemId}`);
    }

    app.patchAppState({
      editForm: {
        currentTrackingItem: undefined,
        currentProject: undefined,
        currentReportingItem: undefined,
      },
    });
    projectId &&
      app.navigate(
        generatePath(APP_ROUTES.report, {
          id: projectId,
          viewname: "dashboard",
        })
      );
  }

  function createConfirmationDialog() {
    const confirmationDialog = dialog.addComponent({
      component: PMPopUp,
    });

    confirmationDialog.mapVisibility(
      ({ showEditFormConfirmationDialog }) =>
        showEditFormConfirmationDialog === true
    );

    confirmationDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      return {
        title:
          getLocalizedMessage(language, "dialog.title.unsavedChanges", {
            entity: "form",
          }) || "Unsaved Changes",
        description:
          getLocalizedMessage(language, "dialog.message.unsavedChanges", {
            entity: "form",
          }) ||
          "You have made changes that have not been saved. If you close the settings, these changes will be lost.",
        maxWidth: "sm",

        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.cancel") || "Cancel",
        cancelButtonColor: "white",
        confirmationButtonColor: "blue",
        confirmationButtonText:
          getLocalizedMessage(language, "dialog.button.continue") || "Continue",

        cancelButtonFireEvent: () => closeConfirmationDialog(),
        confirmationButtonFireEvent: () => {
          closeConfirmationDialog();
          closeEditForm();
        },
        onClose: () => closeConfirmationDialog(),
      };
    });

    function closeConfirmationDialog() {
      app.patchAppState({
        showEditFormConfirmationDialog: false,
      });
    }
  }

  function createDeleteTrackingItemDialog() {
    const deleteDialog = dialog.addComponent({
      component: PMPopUp,
    });

    deleteDialog.mapVisibility(
      ({ showDeleteOwnTrackingItemDialog }) =>
        showDeleteOwnTrackingItemDialog === true
    );

    deleteDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      const itemName =
        app.getAppState().settings?.configuration?.itemName ?? "Tracking Item";
      return {
        title: `Delete ${itemName}`,
        description: `Are you sure you want to delete this ${itemName.toLowerCase()}? This action cannot be undone.`,
        maxWidth: "sm",
        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.cancel") || "Cancel",
        cancelButtonColor: "white",
        confirmationButtonColor: "error",
        confirmationButtonText:
          getLocalizedMessage(language, "delete") || "Delete",
        cancelButtonFireEvent: () => closeDeleteDialog(),
        confirmationButtonFireEvent: async () => {
          closeDeleteDialog();
          if (trackingItemId) {
            await deleteBasicItem(String(trackingItemId));
            closeEditForm();
          }
        },
        onClose: () => closeDeleteDialog(),
      };
    });

    function closeDeleteDialog() {
      app.patchAppState({
        showDeleteOwnTrackingItemDialog: false,
      });
    }
  }

  let initialTabSlug = "";

  // The block the dialog opens on counts as visited even before any tab change
  // reports it. A review already saved as ready for submission was worked
  // through block by block, so its complete blocks stay green after a reload.
  const isBlockVisited = (slug: string) => {
    const { settings, editForm: editFormState } = app.getAppState();
    const readyName = getReviewStageName(
      settings?.configuration,
      "readyForSubmission"
    );
    const landingSlug = orderedTabSlugs.includes(initialTabSlug)
      ? initialTabSlug
      : orderedTabSlugs[0];
    return (
      visitedBlockSlugs.has(slug) ||
      slug === landingSlug ||
      (!!readyName &&
        editFormState?.currentReportingItem?.degreeOfCompletion === readyName)
    );
  };

  const route = parentRoute.createChildRoute<"tracking-item-id">({
    path: APP_ROUTES.trackingItemDialog,
    view: dialog,
    isOverlayChild: true,
  });

  const blockRoute = parentRoute.createChildRoute<
    "tracking-item-id" | "block-code"
  >({
    path: APP_ROUTES.trackingItemDialogBlock,
    view: dialog,
    isOverlayChild: true,
  });
  createConfirmationDialog();
  createDeleteTrackingItemDialog();

  function preFillFromAppState() {
    const cachedProject =
      app.getAppState().currentReport?.project ??
      app.getAppState().editForm?.currentProject;
    if (!cachedProject || !trackingItemId) return;
    const cachedRI = cachedProject.reportingItems?.find(
      (ri) => String(ri.basicItem?.id) === String(trackingItemId)
    );
    if (!cachedRI) return;
    app.patchAppState({
      editForm: {
        currentProject: cachedProject,
        currentTrackingItem: cachedRI.basicItem
          ? ({
              ...cachedRI.basicItem,
              // Seed every bindable ReportingItem column, or a bound field renders
              // empty and overwrites the stored value on save.
              ...getReportingItemSeedValues(
                cachedRI as unknown as Record<string, unknown>
              ),
            } as BasicItem)
          : undefined,
        currentReportingItem: cachedRI as ReportingItem | undefined,
      },
    });
  }

  async function handleRouteLoad(tabSlug: string) {
    const formInitializationSerial = ++questionnaireFormInitializationSerial;
    initialTabSlug = tabSlug;
    visitedBlockSlugs = trackingItemId
      ? (visitedBlockSlugsByItem.get(trackingItemId) ?? new Set())
      : new Set();
    if (trackingItemId) {
      visitedBlockSlugsByItem.set(trackingItemId, visitedBlockSlugs);
    }

    let currentProject: Project | undefined;
    try {
      if (projectId && projectId !== "new-project") {
        const result = await fetchProject({ id: projectId });
        currentProject = result?.project;
        if (!currentProject) {
          console.error(
            `[TrackingItemForm] fetchProject returned empty result for projectId: ${projectId}`
          );
        }
      }
    } catch (err) {
      console.error(
        `[TrackingItemForm] Error fetching project ${projectId}:`,
        err
      );
    }

    let configuration: Configuration | undefined;
    try {
      configuration = await fetchEntityConfiguration();
      if (!configuration) {
        console.error(
          "[TrackingItemForm] fetchEntityConfiguration returned empty"
        );
      }
    } catch (err) {
      console.error("[TrackingItemForm] Error fetching configuration:", err);
    }

    const oldSettings = app.getAppState().settings;
    const existingReportingItem = currentProject?.reportingItems?.find(
      (ri) => String(ri.basicItem?.id) === String(trackingItemId)
    );

    app.patchAppState({
      showEditFormConfirmationDialog: false,
      editForm: {
        currentProject: currentProject,
        currentTrackingItem: existingReportingItem?.basicItem
          ? ({
              ...existingReportingItem.basicItem,
              // Seed every bindable ReportingItem column, or a bound field renders
              // empty and overwrites the stored value on save.
              ...getReportingItemSeedValues(
                existingReportingItem as unknown as Record<string, unknown>
              ),
            } as BasicItem)
          : undefined,
        currentReportingItem: existingReportingItem as
          ReportingItem | undefined,
      },
      settings: {
        ...oldSettings,
        configuration: configuration,
      },
    });

    form.mapState((appState) => {
      const activeConfiguration = appState.settings?.configuration;
      const currentReportingItem = appState.editForm?.currentReportingItem;

      const doneName = getReviewStageName(activeConfiguration, "submitted");
      const isAlreadySubmitted =
        !!doneName && currentReportingItem?.degreeOfCompletion === doneName;

      const completionEnabled = Boolean(
        activeConfiguration?.fieldsDefinition
          ?.filter((f) => f.purpose === "itemReporting")
          ?.find((f) => f.fieldName === "completion")?.enabled
      );
      const progressEnabled = Boolean(
        activeConfiguration?.fieldsDefinition
          ?.filter((f) => f.purpose === "itemReporting")
          ?.find((f) => f.fieldName === "progress")?.enabled
      );
      const titleBadge = appState.editForm?.currentTrackingItem ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {completionEnabled && (
            <Badge
              value={currentReportingItem?.degreeOfCompletion}
              definitions={activeConfiguration?.degreeOfCompletionDefinition}
            />
          )}
          {progressEnabled && (
            <ProgressPieBadge value={currentReportingItem?.progress} />
          )}
        </Box>
      ) : undefined;

      return {
        ...{
          ...(appState.editForm?.currentTrackingItem ?? {
            id: uuidv4(),
            flag: "create",
            projectSpecificItem: true,
            startDate: new Date().toISOString().split("T")[0],
            endDate: new Date().toISOString().split("T")[0],
            // Question bound to reporting-item.dcRelevance prefills to "Yes"
            // for a brand-new item, so the rest of the questionnaire starts visible.
            dcRelevance: "Yes",
          }),
        },
        // Read-only mirror of the OE, for questions bound to project.* targets.
        project: {
          name: appState.editForm?.currentProject?.name,
          customId: appState.editForm?.currentProject?.customId,
        },
        ...{
          validateFormData: (data: Omit<BasicItem, "id">) =>
            validateBasicItemForm(data, configuration, {
              fieldRequired:
                getLocalizedMessage(
                  appState.language || "en",
                  "validationErrors.fieldRequired"
                ) || "This field is required",
              dateNotValid:
                getLocalizedMessage(
                  appState.language || "en",
                  "validationErrors.dateNotValid"
                ) || "This date is not valid",
              endDateAfterStartDate:
                getLocalizedMessage(
                  appState.language || "en",
                  "validationErrors.endDateAfterStartDate"
                ) || "End date should be after start date",
            }),
        },
        title: appState.editForm?.currentTrackingItem
          ? `Review: ${appState.editForm.currentTrackingItem.customId ?? ""} - ${
              appState.editForm.currentTrackingItem.name ?? ""
            }${getCreatedBySuffix(appState.editForm.currentTrackingItem)}`
          : `Create ${getItemCreationDisplayName(
              appState.settings?.configuration?.itemName
            )}`,
        titleBadge,
        enableCloseButton: true,
        showTabCompletionIndicator: true,
        initialSelectedTabId: initialTabSlug,
        saveOnAction: { next: true },
        validationTarget: "none",
        disableNavigationWithoutTarget: true,
        disableSaveWithoutEdits: true,
        disableSubmitUntilComplete: true,
        actions: {
          "save-and-back": {
            sx: { color: (theme) => theme.palette.primary.main },
            visible: true,
            label:
              getLocalizedMessage(appState.language || "en", "back") || "Back",
            ...clearSubmitPressedHandlers,
          },
          "save-and-next": {
            sx: {
              color: (theme) => theme.palette.primary.main,
              backgroundColor: "white",
            },
            endIcon: <ArrowForward />,
            slot: "left",
            label:
              getLocalizedMessage(appState.language || "en", "saveAndNext") ||
              "Save & Next",
            variant: "text",
            visible: true,
            ...clearSubmitPressedHandlers,
          },
          save: {
            sx: {
              color: "white",
              backgroundColor: (theme) => theme.palette.primary.main,
              boxShadow: "none",
              "&.Mui-disabled": {
                backgroundColor: (theme) =>
                  theme.palette.action.disabledBackground,
                color: (theme) => theme.palette.action.disabled,
              },
            },
            label:
              getLocalizedMessage(appState.language || "en", "save") || "Save",
            visible: true,
            variant: "contained",
            ...clearSubmitPressedHandlers,
            // Disabled while nothing has changed; the form stays open on save.
          },
          // Leaving without finalizing is Save plus Cancel, so the form keeps the
          // five buttons the spec asks for and drops this one.
          "save-and-close": { visible: false },
          // Stays disabled until every block is complete. It can still refuse on
          // click once enabled, since a filled field can be invalid; that path
          // fires `submit-blocked` so the overarching error can be shown.
          submit: {
            sx: {
              color: "white",
              backgroundColor: "#F89500",
              "&.Mui-disabled": {
                backgroundColor: (theme) =>
                  theme.palette.action.disabledBackground,
                color: (theme) => theme.palette.action.disabled,
              },
            },
            label:
              getLocalizedMessage(appState.language || "en", "submit") ||
              "Submit",
            disabled: isAlreadySubmitted,
            overrideValidationTarget: "all-tabs",
            visible: true,
            variant: "contained",
            slot: "right",
            ...submitPressedHandlers,
          },

          cancel: {
            variant: "text",
            sx: {
              color: (theme) => theme.palette.primary.main,
              backgroundColor: "transparent",
              boxShadow: "none",
              "&:hover": { backgroundColor: "transparent" },
            },
            label:
              getLocalizedMessage(appState.language || "en", "cancel") ||
              "Cancel",
          },

          // Only the OE user who manually created this item may delete it;
          // hidden for imported items and for anyone else's items.
          delete: {
            visible: isOwnManualTrackingItem(
              appState.editForm?.currentTrackingItem,
              appState.userProfile?.email
            ),
            label:
              getLocalizedMessage(appState.language || "en", "delete") ||
              "Delete",
          },
        },
        formData: appState.editForm?.currentReportingItem?.formData ?? {},
        onTabChange: (tabId: string) => {
          visitedBlockSlugs.add(tabId);
          const idx = orderedTabSlugs.indexOf(tabId);
          if (idx >= 0) {
            currentTabSlugIndex = idx;
            syncUrlToCurrentTab();
          }
          requestAnimationFrame(() => {
            const paper = document.querySelector(".MuiDialog-paper");
            if (!(paper instanceof HTMLElement)) return;
            paper.scrollTop = 0;
            paper.querySelectorAll("*").forEach((el) => {
              if (!(el instanceof HTMLElement)) return;
              const overflowY = window.getComputedStyle(el).overflowY;
              if (overflowY === "auto" || overflowY === "scroll") {
                el.scrollTop = 0;
              }
            });
          });
        },
      };
    });
    const buildQuestionnaireTabs = async () => {
      orderedTabSlugs = [];
      const appState = app.getAppState();
      const configuration = appState.settings?.configuration;
      const doneName = getReviewStageName(configuration, "submitted");
      const isAlreadySubmitted =
        !!doneName &&
        appState.editForm?.currentReportingItem?.degreeOfCompletion ===
          doneName;

      // Questionnaire blocks — one tab per enabled block, for all use-cases.
      // Block 1 carries the master-data fields (name, distribution channel, …)
      // as entity-ref-bound questions, so there is no separate Master Data tab.
      const blueprint = configuration?.trackingItemForms?.[0];
      if (!blueprint) {
        console.error(
          "[TrackingItemForm] No blueprint found. Configuration:",
          configuration,
          "trackingItemForms:",
          configuration?.trackingItemForms
        );
      }
      if (blueprint) {
        const overriddenLlmQuestionIds = new Set<string>(
          Object.keys(
            appState.editForm?.currentReportingItem?.llmSuggestedFormData ?? {}
          )
        );
        const initialLlmAnswersByQuestionId = getInitialLlmAnswersByQuestionId(
          blueprint.blocks,
          appState.editForm?.currentTrackingItem,
          appState.editForm?.currentReportingItem
        );
        // Built over every block, not just the enabled ones, so a condition can
        // point at a bound question in any block.
        const boundFormKeys = buildBoundFormKeys(blueprint.blocks);
        const entityRefSourceOverrides = buildEntityRefSourceOverrides(
          blueprint.blocks,
          appState.editForm?.currentReportingItem?.formData,
          appState.editForm?.currentTrackingItem?.customId
        );
        const isSubmissionBlock = (code?: string) => {
          const normalized = code?.trim().toLowerCase();
          return (
            normalized === SUBMISSION_BLOCK_CODE ||
            normalized === LEGACY_SUBMISSION_BLOCK_CODE
          );
        };
        const sortedBlocks = [...blueprint.blocks]
          .filter((b) => b.enabled)
          .sort((a, b) => {
            const aSubmission = isSubmissionBlock(a.code);
            const bSubmission = isSubmissionBlock(b.code);
            if (aSubmission !== bSubmission) {
              return aSubmission ? 1 : -1;
            }
            return a.order - b.order;
          });
        blockTabs = [];
        sortedBlocks.forEach((block, index) => {
          const isSubmissionBlockTab = isSubmissionBlock(block.code);
          const blockSlug = block.code ?? block.id;
          orderedTabSlugs.push(blockSlug);
          // A block reopened by activating a new sibling block only reopens
          // that new block: one already locked in at the item's last
          // submission (its id snapshotted into lockedBlockIds) stays
          // read-only even once the item's overall stage drops back to
          // "in progress". The Submission tab has no data of its own to
          // lock — it's a live summary of the item's overall status — so
          // it must always follow `isAlreadySubmitted` directly rather than
          // a stale snapshot.
          const blockLocked =
            isAlreadySubmitted ||
            (!isSubmissionBlockTab &&
              !!appState.editForm?.currentReportingItem?.lockedBlockIds?.includes(
                block.id
              ));
          const dcRelevanceModifiedImportedAt =
            block.title.trim().toLowerCase() === DC_RELEVANCE_BLOCK_TITLE
              ? appState.editForm?.currentReportingItem
                  ?.dcRelevanceModifiedImportedAt
              : undefined;
          const blockTab = editForm.addFormTab({
            label:
              !isSubmissionBlockTab && block.code
                ? `${String(block.code).replace(/^B/, "")} - ${block.title}`
                : block.title,
            id: blockSlug,
            isVisible: (formValues) =>
              isBlockVisible(block, formValues, boundFormKeys),
          });
          blockTabs.push(blockTab);
          applyReviewBlockColors(
            blockTab,
            () => blockLocked,
            () => {
              const dirtyFields =
                editForm.getFormMethods()?.formState.dirtyFields;
              return (
                !!dirtyFields &&
                blockTab.formFields.some(
                  (field) => "name" in field && get(dirtyFields, field.name)
                )
              );
            },
            () => isBlockVisited(blockSlug)
          );
          blockTab.addFormField({
            type: QUESTIONNAIRE_BLOCK_HEADER_FIELD_TYPE,
            name: `__header_${block.id}`,
            blockTitle: block.title,
            blockCode: isSubmissionBlockTab ? undefined : block.code,
            blockDescription: block.description,
            uiOptions: { excludeFromCompletion: true },
          });
          buildQuestionnaireBlockFields(blockTab, block, "formData", {
            configuration,
            oeChannelIds:
              appState.editForm?.currentProject?.distributionChannels,
            boundFormKeys,
            overriddenLlmQuestionIds,
            initialLlmAnswersByQuestionId,
            entityRefSourceOverrides,
            getValues: (fieldName) =>
              getQuestionnaireFieldValue(editForm.getFormMethods(), fieldName),
            dirtyFields: editForm.getFormMethods()?.formState.dirtyFields,
            isAlreadySubmitted: blockLocked,
            forceReadOnly: blockLocked,
            isCreateMode: !appState.editForm?.currentTrackingItem,
            isOwnManualItem: isOwnManualTrackingItem(
              appState.editForm?.currentTrackingItem,
              appState.userProfile?.email
            ),
            manualItemCreatedBy:
              appState.editForm?.currentTrackingItem?.source ===
              APP_ITEM_SOURCES.MANUAL
                ? appState.editForm?.currentTrackingItem?.createdBy
                : undefined,
          });
          if (block.title.trim().toLowerCase() === DC_RELEVANCE_BLOCK_TITLE) {
            blockTab.addFormField({
              name: DC_RELEVANCE_COMMENT_FIELD_NAME,
              label: "Comments",
              type: "text",
              multiline: true,
              placeholder: DC_RELEVANCE_COMMENT_PLACEHOLDER,
              sx: {
                "& .MuiInputBase-inputMultiline, & textarea": {
                  fontSize: "14px",
                },
                "& textarea": {
                  minHeight: "clamp(75px, 12vh, 75px)",
                  resize: "vertical",
                },
              },
              uiOptions: { fieldSize: { xs: 12 } },
              ...(blockLocked ? { disabled: true } : {}),
            });
            if (dcRelevanceModifiedImportedAt) {
              blockTab.addFormField({
                type: DC_RELEVANCE_IMPORT_INFO_FIELD_TYPE,
                name: `__dc_relevance_import_info_${block.id}`,
                modifiedAt: dcRelevanceModifiedImportedAt,
                uiOptions: {
                  fieldSize: { xs: 12 },
                  excludeFromCompletion: true,
                },
              });
            }
          }
          if (isSubmissionBlockTab) {
            blockTab.addFormField({
              type: QUESTIONNAIRE_SUBMISSION_STATUS_FIELD_TYPE,
              name: `__submission_status_${block.id}`,
              label: "Submission Status",
              getValue: () =>
                app.getAppState().editForm?.currentReportingItem
                  ?.degreeOfCompletion,
              getDefinitions: () =>
                app.getAppState().settings?.configuration
                  ?.degreeOfCompletionDefinition,
              definitions: configuration?.degreeOfCompletionDefinition,
              // Shows a badge and takes no answer, so it must not keep its own
              // block from ever counting as complete (which would leave Submit
              // permanently disabled).
              uiOptions: { excludeFromCompletion: true },
            });
          } else {
            blockTab.addFormField({
              type: QUESTIONNAIRE_BLOCK_COMPLETION_HINT_FIELD_TYPE,
              name: `__completion_hint_${block.id}`,
              blockNumber: index + 1,
              getCompletionStatus: () => getDisplayCompletionStatus(blockTab),
              getAllBlocksComplete: () =>
                blockTabs.every(
                  (t) => getDisplayCompletionStatus(t) === "complete"
                ),
              getSubmitBlocked: () => submitBlocked,
              isAlreadySubmitted: blockLocked,
              uiOptions: {
                fieldSize: { xs: 12 },
                pinnedToBottom: true,
                excludeFromCompletion: true,
              },
            });
          }
        });
      }
    };
    rebuildQuestionnaireTabs = () => {
      questionnaireTabsBuildChain = questionnaireTabsBuildChain.then(
        async () => {
          if (
            formInitializationSerial !== questionnaireFormInitializationSerial
          ) {
            return;
          }
          await editForm.buildDynamicTabsAsynchronously(buildQuestionnaireTabs);
        }
      );
      return questionnaireTabsBuildChain;
    };
    await rebuildQuestionnaireTabs();
    stopQuestionnaireFieldWatcher?.();
    stopQuestionnaireFieldWatcher = setupFormFieldWatcher({
      getFormMethods: () =>
        getQuestionnaireWatcherFormMethods(editForm.getFormMethods()),
      rules: [{ match: /.+/, onChange: () => void rebuildQuestionnaireTabs() }],
    });
    const rebuildAfterFormInitialization = () => {
      if (formInitializationSerial !== questionnaireFormInitializationSerial) {
        return;
      }
      if (!editForm.getFormMethods()) {
        requestAnimationFrame(rebuildAfterFormInitialization);
        return;
      }
      void rebuildQuestionnaireTabs();
    };
    requestAnimationFrame(rebuildAfterFormInitialization);
    if (!orderedTabSlugs.includes(initialTabSlug)) {
      initialTabSlug = orderedTabSlugs[0] ?? initialTabSlug;
    }
    currentTabSlugIndex = Math.max(0, orderedTabSlugs.indexOf(initialTabSlug));
    if (initialTabSlug) visitedBlockSlugs.add(initialTabSlug);
  }

  route.on("load", async ({ payload }) => {
    projectId = window.location.pathname.split("/")[2];
    trackingItemId = payload?.parameters?.["tracking-item-id"];
    distributionChannel =
      new URLSearchParams(window.location.search).get("channel") ?? undefined;
    // The dialog is one long-lived closure reused across every tracking item, so
    // a stale Submit press from a previous item must not leak into this one.
    clearSubmitPressed();
    preFillFromAppState();
    await handleRouteLoad("");
  });

  blockRoute.on("load", async ({ payload }) => {
    projectId = window.location.pathname.split("/")[2];
    trackingItemId = payload?.parameters?.["tracking-item-id"];
    distributionChannel =
      new URLSearchParams(window.location.search).get("channel") ?? undefined;
    clearSubmitPressed();
    preFillFromAppState();
    const blockCode = payload?.parameters?.["block-code"] ?? "";
    await handleRouteLoad(blockCode);
  });
}
